<template>
  <div class="family-group-member-list">
    <LoadingIndicator v-if="!initialLoadCompleteRef"/>
    <div v-else-if="!canListMembersRef" class="alert alert-primary alert-sm">
      <LockIcon/> You do not have permission to see the members of this group.
    </div>
    <template v-else>
      <div class="help-text">
        <template v-if="canAddMembersRef">
          Choose which people should be visible to the group, then invite people to join.
          <button type="button" class="btn btn-inline btn-link" data-bs-toggle="modal" data-bs-target="#sharingTipModal">
            <EditIcon/>
            Learn more
          </button>
        </template>
        <template v-else>
          Managed profiles shared with the group and users that have joined the group.
        </template>
      </div>
      <div v-if="showSharingLimitAlertRef" class="alert alert-primary alert-sm">
        Users added to this group count toward the sharing limit for your account.
        <button type="button" class="btn btn-inline btn-link" data-bs-toggle="modal" data-bs-target="#sharingLimitModal">
          View current sharing
        </button>
      </div>
      <div class="list-area">
        <div v-if="showListOptionsRef || showListActionsRef" class="list-actions">
          <div class="view-options">
            <div v-if="showListModeOptionRef" class="btn-group btn-group-sm" role="group">
              <input type="radio" class="btn-check" name="listMode" id="listModeRdo" autocomplete="off" v-model="listModeRef" :value="true">
              <label class="btn btn-outline-secondary" for="listModeRdo" title="Simple list">
                <AlignJustifyIcon/>
              </label>
              <input type="radio" class="btn-check" name="listMode" id="groupModeRdo" autocomplete="off" v-model="listModeRef" :value="false">
              <label class="btn btn-outline-secondary" for="groupModeRdo" title="Group members by generation">
                <AlignRightIcon/>
              </label>
            </div>
            <div v-if="showSuggestedOptionRef" class="btn-group btn-group-sm" role="group">
              <input type="checkbox" class="btn-check" id="showSuggestedChk" v-model="showSuggestedRef"/>
              <label class="btn btn-outline-secondary" for="showSuggestedChk" title="Show suggested members">
                <img src="@/assets/icons/person_dotted_80.png"/>
              </label>
            </div>
          </div>
          <div v-if="showListActionsRef" class="btn-group btn-group-sm">
            <button class="btn btn-outline-secondary" @click="showAddPersonModal">
              Add someone
            </button>
          </div>
        </div>
        <ul v-if="showRelationshipsRef" class="category-list list-unstyled">
          <li v-for="rel in orderedCategories" :key="rel">
            <ExpandableItem 
              v-if="shouldShowCategory(rel)"
              :checkbox="isSharingModeRef"
              :checked="categorySelectState(rel) === true"
              :indeterminate="categorySelectState(rel) === undefined"
              :checkDisabled="!canSelectCategory(rel)"
              expanded
              @check="toggleCategorySelected(rel)">
              <template #item>
                <h5 class="category-item">
                  {{ categoryName(rel) }}
                  <span class="category-subtext">({{ categoryListPersons(rel).length }})</span>
                </h5>
              </template>
              <template #content>
                <ul class="member-list list-unstyled" :class="{ 'show-select': isSharingModeRef }">
                  <li v-for="listPerson in categoryListPersons(rel)" :key="listPerson.id">
                    <FamilyGroupMemberListPerson
                      :family-group-id="familyGroupId"
                      :list-person="listPerson"
                      :allow-add="canAddMembersRef"
                      :sharingMode="isSharingModeRef"
                      :suggested-parent="isSuggestedParent(listPerson)"
                      @member-invite="onInviteMember"
                      @member-click="onMemberClick"/>
                  </li>
                </ul>
              </template>
            </ExpandableItem>
          </li>
        </ul>
        <ul v-else class="member-list list-unstyled">
          <li v-for="listPerson in visibleListPersonsRef" :key="listPerson.id">
            <FamilyGroupMemberListPerson
              :family-group-id="familyGroupId"
              :list-person="listPerson"
              :allow-add="canAddMembersRef"
              :sharingMode="isSharingModeRef"
              :suggested-parent="isSuggestedParent(listPerson)"
              @member-invite="onInviteMember"
              @member-click="onMemberClick"/>
          </li>
        </ul>
      </div>
    </template>
    <SharingLimitModal id="sharingLimitModal"/>
    <Modal id="sharingTipModal" info>
      <template #icon>
        <img src="@/assets/icons/everyone_500.png" class="modal-icon"/>
      </template>
      <template #title>Group members</template>
      <div class="sharing-tip-body">
        <div>
          A group member is someone whose profile is visible to the group, or has joined the group.
        </div>
        <ul>
          <li>Managed profiles are created automatically for people in the "living window"</li>
          <li>The living window includes anyone born in the last 110 years</li>
          <li>Profiles in the living window are restricted by default and must be shared with a group to be visible to others</li>
          <li>The group owner can share any profiles they manage with the group</li>
          <li>The group owner can invite others to share profiles they manage with the group</li>
          <li>Group members with permission to invite others to the group can also share profiles managed by the group owner with the group</li>
          <li>Group members with permission to invite others can request others to share profiles they manage with the group</li>
          <li>Group members should be able to see themselves and their parents and grandparents in the tree</li>
          <li>To respect privacy, make sure people are only in a group with people they already know</li>
        </ul>
        <TipAlert small>
          To make different people visible to different sets of family members, use multiple groups.
        </TipAlert>
      </div>
    </Modal>
    <FamilyGroupMemberModal ref="memberModalRef" 
      :family-group-id="familyGroupId"
      @share-profile="onShareProfile"
      @remove-profile="onRemoveProfile"
      @invite="onInviteMember"
      @add-user="onAddUser"
      @view-sharing-invite="onShowSharingInvite"
      @view-group-invite="onShowMemberInvite"
      @remove-user="onRemoveUser"/>
    <DataGroupInviteModal ref="dataGroupInviteModalRef"/>
    <FamilyGroupInviteModal ref="inviteModalRef"/>
    <SharingLimitReachedModal ref="sharingLimitReachedModalRef"/>
    <FamilyGroupAddOtherModal
      ref="addOtherModalRef"
      :family-group-id="familyGroupId"
      :potential-members="potentialMembers"
      @invite="onInviteOther"
      @add-new="addNewModalRef?.open()"/>
    <ProfileAddModal ref="addNewModalRef" @added="onProfileAdded">
      <template #title>
        Add someone new
      </template>
      <template #instructions>
        Add someone who is not an existing user or person in your tree.
      </template>
    </ProfileAddModal>
    <FamilyGroupMemberRemoveModal ref="removeModalRef" @ok="removeMember"/>
    <UserAddModal ref="userAddModalRef"/>
  </div>
</template>

<style lang="scss" scoped>
.family-group-member-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.loading-indicator {
  margin-left: 1rem;
  font-size: 0.875rem;
  color: #888;
}

.alert {
  margin: 0;
}

.help-text .btn-inline {
  margin-left: 0.25rem;
}

$mode-active-bg: #f8f8f8;
$mode-active-padding: 1rem;

.mode-header {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  border: 1px solid #ccc;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  border-bottom: none;
  background-color: $mode-active-bg;
  padding: $mode-active-padding;

  .actions {
    display: flex;
    gap: 0.5rem;
    font-size: 0.875rem;

    .btn {
      min-width: 80px;
    }
  }
}

.list-area {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: flex-start;

  .alert, .tip-alert {
    align-self: stretch;
    margin: 0;
  }

  .tip-alert {
    ul {
      margin: 0.25rem 0 0;
    }
  }

  .category-list .member-list {
    margin-left: 1.5rem;
  }
}

.list-actions {
  margin-top: 0.25rem;
  display: grid;
  grid-template-columns: repeat(3, max-content);
  column-gap: 0.5rem;
  row-gap: 1rem;
  font-size: 0.875rem;

  .view-options {
    display: flex;
    gap: 0.5rem;

    @media (max-width: 768px){
      grid-row: 2;
      grid-column: 1 / span 3;
      justify-self: start;
    }
  }

  #showSuggestedChk + label {
    padding: 0.25rem calc(0.5rem - 2px);

    img {
      width: 20px;
    }
  }

  .show-suggested label:hover img {
    filter: brightness(0) saturate(100%) invert(26%) sepia(86%) saturate(2319%) hue-rotate(209deg) brightness(100%) contrast(99%);
  }

  .invite-mode img {
    margin-right: 0.25rem;
    width: 20px;
    filter: invert(1);
  }
}

.member-list {
  padding-bottom: 1rem;
}

.list-area > .member-list {
  margin-left: 1.5rem;
}

input[type="checkbox"] {
  margin-right: 0.5rem;
}

.category-item {
  margin: 0 0 0.25rem;
  font-weight: inherit;
  font-size: 1rem;
  color: #888;

  .category-subtext {
    margin-left: 2px;
    color: #999;
    font-size: 0.875rem;
  }
}

#sharingTipModal {
  .modal-icon {
    width: 40px;
    margin-right: 0.5rem;
  }

  .sharing-tip-body {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    ul {
      margin: 0 1rem;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      font-size: 0.875rem;
    }

    .tip-alert {
      margin: 0;
      font-size: 0.875rem;
    }
  }
}

</style>

<script lang="ts" setup>
import { computed, ref, reactive, watch, onBeforeMount, provide } from 'vue'
import { DataGroupMember, GroupMemberRole, ItemPermissions, PrincipalType } from '@/gp/GroupAdminModel'
import { livingWindowStart } from '@/rd/ResearchDataModel'
import { useUserStore } from '@/gp/UserStore'
import { useDataGroupMemberStore } from '@/gp/DataGroupMemberStore'
import { useDataGroupInviteStore } from '@/gp/DataGroupInviteStore'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { useFamilyGroupStore } from '@/gp/FamilyGroupStore'
import { useFamilyGroupMemberStore } from '@/gp/FamilyGroupMemberStore'
import { useFamilyGroupInviteStore } from '@/gp/FamilyGroupInviteStore'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import { useViewFamilyStore } from '@/rd/ViewFamilyStore'
import { useTokenManager } from '@/auth/TokenManager'
import { SharingUser, useSharingLimiter } from './SharingLimiter'
import { useProfileSharingState } from './ProfileSharingState'
import { RelationshipCategory, PotentialMember, MaxAncestorGenerations, useFamilyGroupMemberManager, MemberListMode } from './FamilyGroupMemberManager'
import { AlignJustifyIcon, AlignRightIcon, EditIcon, LockIcon } from '@zhuowenli/vue-feather-icons'
import { LoadMode } from '@/util/AsyncData'
import { isDefined } from '@/util/TypeScriptUtil'
import { PatchChange } from '@/util/Api'
import { remove, uniq, uniqBy } from 'lodash'
import { ListPerson } from './FamilyGroupMemberListPerson'
import { getMultiSelectState } from '@/util/InputUtil'
import { refDebounced } from '@vueuse/core'
import LoadingIndicator from '@/util/LoadingIndicator.vue'
import ExpandableItem from '@/util/ExpandableItem.vue'
import FamilyGroupMemberListPerson from './FamilyGroupMemberListPerson.vue'
import FamilyGroupMemberModal from './FamilyGroupMemberModal.vue'
import FamilyGroupInviteModal from './FamilyGroupInviteModal.vue'
import FamilyGroupAddOtherModal from './FamilyGroupAddOtherModal.vue'
import FamilyGroupMemberRemoveModal from './FamilyGroupMemberRemoveModal.vue'
import DataGroupInviteModal from '@/manage/datagroups/DataGroupInviteModal.vue'
import UserAddModal from '@/manage/users/UserAddModal.vue'
import SharingLimitModal from './SharingLimitModal.vue'
import TipAlert from '@/util/TipAlert.vue'
import SharingLimitReachedModal from './SharingLimitReachedModal.vue'
import ProfileAddModal from '@/manage/profiles/ProfileAddModal.vue'
import { CompositeId } from '@/rd/CompositeId'
import Modal from '@/util/Modal.vue'

const props = defineProps<{
  familyGroupId: string
  disableListActions?: boolean
}>()

const initialLoadCompleteRef = ref(false)
const loadingRef = ref(false)
const potentialMembers = reactive([]) as PotentialMember[]
const listPersons = reactive([]) as ListPerson[]
const relationshipCategories = reactive(new Map<string, RelationshipCategory>())
const suggestedParentIds = reactive(new Set<string>())
const listModeRef = ref(false)
const showSuggestedRef = ref(false)
const sharingUsersRef = ref<SharingUser[]>([])

const memberModalRef = ref<InstanceType<typeof FamilyGroupMemberModal>>()
const removeModalRef = ref<InstanceType<typeof FamilyGroupMemberRemoveModal>>()
const dataGroupInviteModalRef = ref<InstanceType<typeof DataGroupInviteModal>>()
const inviteModalRef = ref<InstanceType<typeof FamilyGroupInviteModal>>()
const addOtherModalRef = ref<InstanceType<typeof FamilyGroupAddOtherModal>>()
const addNewModalRef = ref<InstanceType<typeof ProfileAddModal>>()
const userAddModalRef = ref<InstanceType<typeof UserAddModal>>()
const sharingLimitReachedModalRef = ref<InstanceType<typeof SharingLimitReachedModal>>()

const userStore = useUserStore()
const familyGroupStore = useFamilyGroupStore()
const familyGroupMemberStore = useFamilyGroupMemberStore()
const memberManager = useFamilyGroupMemberManager()
const viewPersonStore = useViewPersonStore();
const viewFamilyStore = useViewFamilyStore()
const dataGroupMemberStore = useDataGroupMemberStore()
const dataGroupStore = useDataGroupStore()
const dataGroupInviteStore = useDataGroupInviteStore()
const userInviteStore = useFamilyGroupInviteStore()
const sharingLimiter = useSharingLimiter()
const profileSharingState = useProfileSharingState(() => props.familyGroupId)
const tokenManager = useTokenManager()

const asyncFamilyGroupRef = computed(() => familyGroupStore.getAsyncGroup(props.familyGroupId, LoadMode.EnsureLoaded))
const allUserMembersRef = computed(() => familyGroupMemberStore.getMembersForFamilyGroups([props.familyGroupId]))
const allUserInvitesRef = computed(() => userInviteStore.getInvitesForFamilyGroups([props.familyGroupId]))
const allSharingMembersRef = computed(() => dataGroupMemberStore.getMembersForFamilyGroups([props.familyGroupId]))
const allSharingMembersStableRef = refDebounced(allSharingMembersRef, 500)
const allSharingInvitesRef = computed(() => dataGroupInviteStore.getInvitesForFamilyGroup(props.familyGroupId))

const selfRef = computed(() => userStore.getAsyncSelf()?.data)
const familyGroupRef = computed(() => asyncFamilyGroupRef.value?.data)
const scopeFamilyIdRef = computed(() => familyGroupRef.value?.scopeFamilyId)
const ownerUserRef = computed(() => userStore.getAsyncUser(familyGroupRef.value?.ownerId, LoadMode.EnsureLoaded)?.data)
const isOwnerRef = computed(() => familyGroupRef.value?.ownerId == tokenManager.current.userId)

const sharedDataGroupsRef = computed(() => {
  const sharedGroupIds = allSharingMembersStableRef.value?.map(m => m.dataGroupId) ?? []
  return dataGroupStore.getAsyncGroupList(sharedGroupIds, LoadMode.EnsureLoaded).map(a => a.data).filter(isDefined)
})
const sharedProfileGroupsRef = computed(() => sharedDataGroupsRef.value.filter(dg => dg.isProfile))
const sharedResearchGroupsRef = computed(() => sharedDataGroupsRef.value.filter(dg => !dg.isProfile))
const sharedResearchGroupOwnersRef = computed(() => {
  const ownerIds = uniq(sharedResearchGroupsRef.value.map(dg => dg.ownerId))
  return userStore.getAsyncUserList(ownerIds, LoadMode.EnsureLoaded).map(a => a.data).filter(isDefined) // should be loaded with data groups
})
const isSharingLimitedRef = computed(() => sharedResearchGroupOwnersRef.value.some(u => u.limits?.sharingLimit))
const showSharingLimitAlertRef = computed(() => canAddMembersRef.value && isSharingLimitedRef.value && sharingUsersRef.value.length)
const ownerSharingLimitRef = computed(() => ownerUserRef.value?.limitsDisabled ? Infinity : ownerUserRef.value?.limits?.sharingLimit ?? Infinity)
const hasReachedSharingLimitRef = computed(() => isOwnerRef.value && sharingUsersRef.value.length >= ownerSharingLimitRef.value)

const showListModeOptionRef = computed(() => someRelatedRef.value)
const showSuggestedOptionRef = computed(() => !!scopeFamilyIdRef.value && hasSuggestedRef.value)
const hasSuggestedRef = computed(() => listPersons.some(lp => lp.isSuggested))
const isSharingModeRef = computed(() => showSuggestedRef.value && canAddMembersRef.value)
const showListOptionsRef = computed(() => showListModeOptionRef.value || showSuggestedOptionRef.value)
const visibleListPersonsRef = computed(() => listPersons.filter(lp => showSuggestedRef.value || !lp.isSuggested))
const someRelatedRef = computed(() => relationshipCategories.size > 0)
const showRelationshipsRef = computed(() => !listModeRef.value && someRelatedRef.value)
const showListActionsRef = computed(() => canAddMembersRef.value)

const groupPermissionsRef = computed(() => familyGroupStore.getLoadedPermissions(props.familyGroupId)) // loaded by parent
const canListMembersRef = computed(() => (groupPermissionsRef.value ?? 0) & ItemPermissions.ListItems)
const canAddMembersRef = computed(() => (groupPermissionsRef.value & ItemPermissions.AddItems) > 0)

provide('profileSharingState', profileSharingState)

onBeforeMount(() => initialLoadCompleteRef.value = false)

watch(canAddMembersRef, () => showSuggestedRef.value = canAddMembersRef.value, { immediate: true })

// NOTE: calculating sharing users requires many inputs, but to limit dependency tracking, we only watch the ones that can 
// be directly changed in this context
watch([
    allUserMembersRef, 
    allUserInvitesRef
  ], 
  async () => {
    sharingUsersRef.value = [] // reset until we get a result

    if (!isOwnerRef.value)
      return // account sharing only matters for FG owner

    sharingUsersRef.value = await sharingLimiter.getSharingUsersAsync()
}, { immediate: true, deep: true }) // deep to track invitation change (e.g. pending -> cancelled)

watch([
    props,
    scopeFamilyIdRef, // react when FG scope family changed
    allUserMembersRef, // react when FG users added/removed/changed
    allUserInvitesRef, // react when FG user invites added/removed/changed
    allSharingMembersStableRef, // react when shared trees or profiles added/removed/changed
    sharedProfileGroupsRef, // react when properties of shared profile groups change (e.g. isChild, isUserProfile)
    allSharingInvitesRef, // react when sharing invites added/removed/changed
  ],
  async () => {
    console.log('FamilyGroupMemberList: potential members changed')
    await refreshPotentialMembersAsync()
}, { immediate: true, deep: true })

async function refreshPotentialMembersAsync() {
  if (!props.familyGroupId)
    return

  if (loadingRef.value)
    return // ignore nested requests

  loadingRef.value = true

  try {
    console.log('FamilyGroupMemberList: refreshing potential members')
    await asyncFamilyGroupRef.value?.whenLoadCompleted
    if (!familyGroupRef.value)
      return

    // TODO: is this hack necessary, or should other components fix their reactivity to detect new instances?
    const newMembers = await memberManager.getPotentialMembersAsync(familyGroupRef.value)
    console.log('FamilyGroupMemberList: refreshing potential members...done')
    const oldMemberMap = new Map(potentialMembers.map(pm => [pm.id, pm]))
    potentialMembers.length = 0

    for (const newMember of newMembers) {
      const oldMember = oldMemberMap.get(newMember.id)
      if (oldMember) {
        // preserve old instances so reactive effects on other components still work
        Object.assign(oldMember, newMember)
        potentialMembers.push(oldMember) // re-add updated instance
      }
      else {
        potentialMembers.push(newMember) // add new instance
      }
    }

    refreshListPersons()
  }
  finally {
    loadingRef.value = false
    initialLoadCompleteRef.value = true
  }
}

function refreshListPersons() {
  relationshipCategories.clear()
  suggestedParentIds.clear()

  const newListPersons: ListPerson[] = []
  const potentialMembersMap = new Map(potentialMembers.map(pm => [pm.id, pm]))

  const scopeFamily = viewFamilyStore.getAsyncFamily(scopeFamilyIdRef.value)?.data // loaded by member manager
  if (scopeFamily) {
    // NOTE: member manager already loaded view persons for scope family, ancestors, and descendants
    const faFam = [...viewPersonStore.getLoadedAncestors(scopeFamily.fatherId, MaxAncestorGenerations)]
    const moFam = [...viewPersonStore.getLoadedAncestors(scopeFamily.motherId, MaxAncestorGenerations)]

    const scopeFam = viewPersonStore.getLoadedPersonList(scopeFamily.spouseIds)
    
    const children = viewFamilyStore.getLoadedChildren(scopeFamily.id!).children
    const childrenSpouseFamilies = children.flatMap(ch => viewFamilyStore.getLoadedSpouseFamilies(ch.id!).spouseFamilies)
    const childGenerationIds = uniq([...children.map(ch => ch.id!), ...childrenSpouseFamilies.flatMap(sf => sf.spouseIds)])
    const childGeneration = viewPersonStore.getLoadedPersonList(childGenerationIds)

    const grandchildren = childrenSpouseFamilies.flatMap(sf => viewFamilyStore.getLoadedChildren(sf.id!).children)
    const grandchildrenSpouseFamilies = grandchildren.flatMap(ch => viewFamilyStore.getLoadedSpouseFamilies(ch.id!).spouseFamilies)
    const grandchildGenerationIds = uniq([...grandchildren.map(ch => ch.id!), ...grandchildrenSpouseFamilies.flatMap(sf => sf.spouseIds)])
    const grandchildGeneration = viewPersonStore.getLoadedPersonList(grandchildGenerationIds)

    // store the relationships for each view person
    faFam.forEach(vp => relationshipCategories.set(vp.id!, RelationshipCategory.Ancestors))
    moFam.forEach(vp => relationshipCategories.set(vp.id!, RelationshipCategory.Ancestors))
    scopeFam.forEach(vp => relationshipCategories.set(vp.id!, RelationshipCategory.Scope))
    childGeneration.forEach(vp => relationshipCategories.set(vp.id!, RelationshipCategory.Child))
    grandchildGeneration.forEach(vp => relationshipCategories.set(vp.id!, RelationshipCategory.Grandchild))

    for (const vp of [...faFam, ...moFam, ...scopeFam, ...childGeneration, ...grandchildGeneration]) {
      newListPersons.push(ListPerson.fromViewPerson(vp, potentialMembersMap))
    }

    const allPersonIds = new Set(newListPersons.map(lp => lp.viewPerson!.id!))
    const sharedPersonIds = newListPersons.filter(p => p.viewPerson && p.potentialMembers.some(pm => pm.isProfileShared)).map(p => p.viewPerson!.id!)
    let childIds = [...sharedPersonIds]
    while (childIds.length) {
      const parentIds = childIds.flatMap(id => viewPersonStore.getLoadedParentIds(id).parentIds).filter(id => allPersonIds.has(id))
      parentIds.forEach(id => suggestedParentIds.add(id))
      childIds = parentIds
    }
  }

  // ensure potential member view persons added to list
  // NOTE: member manager already loaded view persons for potential members
  const memberPersonIds = potentialMembers.map(pm => pm.profilePerson?.id).filter(isDefined)
  const memberViewPersons = viewPersonStore.getLoadedPersonList(memberPersonIds)
  newListPersons.push(...memberViewPersons.map(vp => ListPerson.fromViewPerson(vp, potentialMembersMap)))

  // ensure user-only potential members (no view person) added to list
  const userOnlyMembers = potentialMembers.filter(pm => !pm.profilePerson) // no visible profile person
  newListPersons.push(...userOnlyMembers.map(ListPerson.fromPotentialMember))

  // drop view persons with no potential member
  remove(newListPersons, listPerson => listPerson.isEmpty)

  // remove duplicates and sort by display name
  const uniquePersons = uniqBy(newListPersons, listPerson => listPerson.id)
  const sortedPersons = uniquePersons.sort((a, b) => a.displayName.localeCompare(b.displayName))

  listPersons.length = 0
  listPersons.push(...sortedPersons)
}

const orderedCategories = [
    RelationshipCategory.Ancestors,
    RelationshipCategory.Scope,
    RelationshipCategory.Child,
    RelationshipCategory.Grandchild,
    RelationshipCategory.None,
]

function categoryName(rel: RelationshipCategory) {
  switch (rel) {
    case RelationshipCategory.Ancestors: return 'Parents and grandparents'
    case RelationshipCategory.Scope: return 'Common ancestors'
    case RelationshipCategory.Child: return 'Children and spouses'
    case RelationshipCategory.Grandchild: return 'Grandchildren and spouses'
    default: return 'Other members'
  }
}

function shouldShowCategory(rel: RelationshipCategory) {
  // hide 'Ancestors' category if empty (e.g. ancestors outside living window)
  return (rel >= RelationshipCategory.Scope) || categoryListPersons(rel).length > 0
}

function canSelectMember(pm: PotentialMember) {
  return pm.isProfileOwner && !pm.hasPendingInvite
}

function isMemberSelected(pm: PotentialMember) {
  return profileSharingState.isShared(pm.profileGroupId)
}

function categoryListPersons(rel: RelationshipCategory) {
  if (rel == RelationshipCategory.None) {
    return visibleListPersonsRef.value.filter(listPerson => !relationshipCategories.has(listPerson.id!))
  }
  else {
    return visibleListPersonsRef.value.filter(listPerson => relationshipCategories.get(listPerson.id!) == rel)
  }
}

function categoryMembers(rel: RelationshipCategory) {
  return categoryListPersons(rel).flatMap(lp => lp.potentialMembers)
}

function canSelectCategory(rel: RelationshipCategory) {
  return categoryMembers(rel).some(canSelectMember)
}

function categorySelectState(rel: RelationshipCategory) {
  const selectableMembers = categoryMembers(rel)//.filter(canSelectMember)
  if (rel == RelationshipCategory.Child && selectableMembers.every(pm => !isMemberSelected(pm))) {
    console.log('doh!')
  }
  return getMultiSelectState(selectableMembers, isMemberSelected)
}

async function toggleCategorySelected(rel: RelationshipCategory) {
  const selectableMembers = categoryMembers(rel).filter(canSelectMember)
  const newValue = !getMultiSelectState(selectableMembers, isMemberSelected) // none/some -> all, all -> none
  profileSharingState.shareProfiles(selectableMembers.map(pm => pm.profileGroupId!), newValue)
}

function isSuggestedParent(listPerson: ListPerson) {
  return listPerson.viewPerson && suggestedParentIds.has(listPerson.viewPerson.id!)
}

function onMemberClick(pm: PotentialMember) {
  memberModalRef.value?.open(pm)
}

function onShowMemberInvite(inviteId: string) {
  inviteModalRef.value?.openEdit(inviteId)
}

function onInviteMember(pm: PotentialMember) {
  if (isSharingLimitedRef.value && hasReachedSharingLimitRef.value) {
    sharingLimitReachedModalRef.value?.open()
  }
  else {
    inviteModalRef.value?.openNew(props.familyGroupId, pm.profileGroup!.startItemId!)
  }
}

function showAddPersonModal() {
  if (isSharingLimitedRef.value && hasReachedSharingLimitRef.value) {
    sharingLimitReachedModalRef.value?.open()
  }
  else {
    addOtherModalRef.value?.open()
  }
}

async function onProfileAdded(profilePersonId: string) {
  const profileGroupId = CompositeId.getGroupId(profilePersonId)!
  const dm = {
    dataGroupId: profileGroupId,
    principalType: PrincipalType.FamilyGroup,
    principalId: props.familyGroupId,
    role: GroupMemberRole.Viewer,
  } as DataGroupMember
  await dataGroupMemberStore.addAsync(profileGroupId, dm)
}

async function onInviteOther(profilePersonId: string, profileOnly: boolean) {
  if (profileOnly) {
    dataGroupInviteModalRef.value?.openNew(CompositeId.getGroupId(profilePersonId)!, props.familyGroupId)
  }
  else {
    inviteModalRef.value?.openNew(props.familyGroupId, profilePersonId)
  }
}

async function onAddUser(pm: PotentialMember) {
  userAddModalRef.value?.open(pm.profileGroupId!)
}

async function onShowSharingInvite(inviteId: string) {
  dataGroupInviteModalRef.value?.openEdit(inviteId)
}

async function onShareProfile(profileGroupId: string) {
  dataGroupInviteModalRef.value?.openNew(profileGroupId!, props.familyGroupId)
}

async function onRemoveProfile(pm: PotentialMember) {
  profileSharingState.shareProfiles([pm.profileGroupId!], false)
}

async function onRemoveUser(pm: PotentialMember) {
  removeModalRef.value?.open(pm)
}

async function removeMember(pm: PotentialMember) {
  if (!pm.member)
    throw new Error('No member to remove')

  await familyGroupMemberStore.deleteAsync(pm.member)
  removeModalRef.value?.close()
}
</script>
